import { useEffect, useRef } from "react";

interface UseModalCommitGuardArgs {
  isOpen: boolean;
  isActiveTab: boolean;
}

/** Guard async modal commits against close/tab-switch/reopen races. */
export function useModalCommitGuard({
  isOpen,
  isActiveTab,
}: UseModalCommitGuardArgs) {
  const latestRef = useRef({ isOpen, isActiveTab });
  useEffect(() => {
    latestRef.current = { isOpen, isActiveTab };
  });
  useEffect(() => {
    return () => {
      latestRef.current.isOpen = false;
    };
  }, []);

  const prevOpenRef = useRef(isOpen);
  const sessionRef = useRef(0);
  if (prevOpenRef.current !== isOpen) {
    prevOpenRef.current = isOpen;
    if (isOpen) sessionRef.current += 1;
  }
  const attemptSessionRef = useRef<number | null>(null);

  const beginAttempt = () => {
    attemptSessionRef.current = sessionRef.current;
  };

  const shouldCommit = () => {
    const latest = latestRef.current;
    return (
      latest.isOpen &&
      latest.isActiveTab &&
      attemptSessionRef.current === sessionRef.current
    );
  };

  return { beginAttempt, shouldCommit };
}
